import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ReportHtml } from 'tms-lib';
import { GoodsDeliveryNote, GoodsDeliveryNoteFindStock, GoodsDeliveryNoteItem } from '../../../models';
import { TmsWebBaseEditService } from '../../base';

@Injectable({
  providedIn: 'root',
})
export class GoodsDeliveryNoteService extends TmsWebBaseEditService<GoodsDeliveryNote, GoodsDeliveryNote> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('GoodsDeliveryNote', $localize`Goods Delivery Note`);
  }

  GetDeliveryItemsForDeliveryNote(
    goodsDeliveryNoteId: string,
    warehouseId: string,
  ): Observable<GoodsDeliveryNoteItem[]> {
    return this.httpService.get<GoodsDeliveryNoteItem[]>(
      `${this.apiUrl}/GetDeliveryItemsForDeliveryNote?goodsDeliveryNoteId=${goodsDeliveryNoteId}&warehouseId=${warehouseId}`,
    );
  }

  getGoodsDeliveryNoteReportHtml(id: string, includeStyles: boolean): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/GoodsDeliveryNoteReportHtml?id=${id}&includeStyles=${includeStyles}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  getCommercialInvoiceReportHtml(id: string, includeStyles: boolean): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/CommercialInvoiceReportHtml?id=${id}&includeStyles=${includeStyles}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  // Persist a single item's edited Commercial Invoice unit price (inline grid edit). Does not affect
  // stock or the order.
  updateCommercialInvoicePrice(item: GoodsDeliveryNoteItem): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/UpdateCommercialInvoicePrice`, item);
  }

  // Scan-to-add lookup for the details screen's Add Stock search (point 14, Steve's visit
  // 2026-07). Returns a status the component switches on - see GoodsDeliveryNoteFindStockStatuses.
  findStockForDeliveryNote(
    stockNo: string,
    goodsDeliveryNoteId: string,
    warehouseId: string,
  ): Observable<GoodsDeliveryNoteFindStock> {
    return this.httpService.get<GoodsDeliveryNoteFindStock>(
      `${this.apiUrl}/FindStockForDeliveryNote?stockNo=${encodeURIComponent(stockNo)}&goodsDeliveryNoteId=${goodsDeliveryNoteId}&warehouseId=${warehouseId}`,
    );
  }

  // The Add Stock search's "already on the note - remove it?" Yes path.
  removeGoodsDeliveryNoteItem(goodsDeliveryNoteId: string, stockItemId: string): Observable<void> {
    return this.httpService.post<void>(`${this.apiUrl}/RemoveGoodsDeliveryNoteItem`, {
      goodsDeliveryNoteId,
      stockItemId,
    });
  }

  getStockForGoodsDeliveryNote(
    search: string,
    warehouseId: string,
    pickingOrders?: boolean,
    stockItems?: boolean,
    salesDespatchItems?: boolean,
  ): Observable<GoodsDeliveryNoteItem[]> {
    return this.httpService.get<GoodsDeliveryNoteItem[]>(
      `${this.apiUrl}/GetStockForGoodsDeliveryNote?search=${search}&warehouseId=${warehouseId}&pickingOrders=${pickingOrders}&stockItems=${stockItems}&salesDespatchItems=${salesDespatchItems}`,
    );
  }

  createGoodsDeliveryNoteFromStock(ids: string[], quantities: number[], warehouseId: string): Observable<string> {
    return this.httpService.post<string>(`${this.apiUrl}/CreateGoodsDeliveryNoteFromStock`, {
      ids,
      quantities,
      warehouseId,
    });
  }

  getCreatedGoodsDeliveryNote(id: string): Observable<GoodsDeliveryNote[]> {
    return this.httpService.get<GoodsDeliveryNote[]>(
      `${this.apiUrl}/GetCreatedGoodsDeliveryNote?goodsDeliveryNoteId=${id}`,
    );
  }

  addGoodsDeliveryNoteItems(ids: string[], quantities: number[], goodsDeliveryNoteId: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/AddGoodsDeliveryNoteItems`, { ids, quantities, goodsDeliveryNoteId });
  }

  receiveStock(ids: string[], comments: string): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/ReceiveStock`, { ids, comments });
  }
}
